import React, { useState } from 'react';
import { TEXT_TERTIARY } from '../theme';

export function parseSeatId(seatId) {
  const parts = seatId.split('_');
  if (parts.length === 3) {
    const sec = parseInt(parts[0], 10) || 0;
    const p2n = parseInt(parts[1], 10);
    const p3n = parseInt(parts[2], 10);
    if (!isNaN(p2n) && !isNaN(p3n)) {
      return { sec, row: p2n, col: p3n };
    }
    const rowIdx = isNaN(p3n)
      ? parts[2].toUpperCase().charCodeAt(0) - 65
      : p3n;
    return { sec, row: rowIdx, col: p2n };
  }
  const m = seatId.match(/^R(\d+)S(\d+)$/);
  if (m) return { sec: 0, row: parseInt(m[1]), col: parseInt(m[2]) };
  return null;
}

function seatColor(seat) {
  if (seat.is_pending) return '#ef4444';
  if (seat.is_approved) return '#6b7280';
  if (seat.in_seatplan && seat.hits === 0) return '#e2e8f0';
  const c = Math.min(1, Math.max(0, seat.confidence));
  if (c < 0.3) return `rgba(147,197,253,${0.5 + c})`;
  if (c < 0.6) return `rgba(251,191,36,${0.55 + c * 0.4})`;
  return `rgba(239,68,68,${0.45 + c * 0.5})`;
}

export default function HallSeatMap({ seatData }) {
  const [tooltip, setTooltip] = useState(null);

  if (!seatData || seatData.length === 0) return null;

  const parsed = [];
  for (const seat of seatData) {
    const pos = parseSeatId(seat.seat_id);
    if (pos) parsed.push({ ...seat, ...pos });
  }
  if (parsed.length === 0) return null;

  const sections = [...new Set(parsed.map((s) => s.sec))].sort((a, b) => a - b);

  const CELL = 13;
  const GAP = 2;
  const STEP = CELL + GAP;

  const sectionLayouts = sections.map((sec) => {
    const seats = parsed.filter((s) => s.sec === sec);
    const uniqueRows = [...new Set(seats.map((s) => s.row))].sort((a, b) => a - b);
    const uniqueCols = [...new Set(seats.map((s) => s.col))].sort((a, b) => a - b);
    const rowIdx = Object.fromEntries(uniqueRows.map((r, i) => [r, i]));
    const colIdx = Object.fromEntries(uniqueCols.map((c, i) => [c, i]));
    return { sec, seats, rowIdx, colIdx, numRows: uniqueRows.length, numCols: uniqueCols.length };
  });

  const SEC_GAP = 20;
  let xOffset = 0;
  const secOffsets = sectionLayouts.map((lay) => {
    const off = xOffset;
    xOffset += lay.numCols * STEP + SEC_GAP;
    return off;
  });
  const totalWidth = Math.max(xOffset - SEC_GAP, 10);
  const maxRows = Math.max(...sectionLayouts.map((l) => l.numRows));
  const totalHeight = maxRows * STEP + 20;

  return (
    <div style={{ overflowX: 'auto', marginTop: 10, direction: 'ltr' }}>
      <div style={{ fontSize: 10, color: TEXT_TERTIARY, textAlign: 'center', marginBottom: 4 }}>— מסך —</div>
      <svg
        width={totalWidth}
        height={totalHeight}
        style={{ display: 'block' }}
        onMouseLeave={() => setTooltip(null)}
      >
        {sectionLayouts.map((lay, si) => {
          const xOff = secOffsets[si];
          return (
            <g key={lay.sec}>
              {lay.seats.map((seat) => {
                const cx = xOff + lay.colIdx[seat.col] * STEP + CELL / 2;
                const cy = lay.rowIdx[seat.row] * STEP + CELL / 2;
                const fill = seatColor(seat);
                const stroke = seat.is_pending ? '#b91c1c' : 'none';
                return (
                  <circle
                    key={seat.seat_id}
                    cx={cx}
                    cy={cy}
                    r={CELL / 2}
                    fill={fill}
                    stroke={stroke}
                    strokeWidth={seat.is_pending ? 1.5 : 0}
                    style={{ cursor: 'pointer' }}
                    onMouseEnter={(e) =>
                      setTooltip({
                        x: e.clientX,
                        y: e.clientY,
                        text: seat.hits > 0
                          ? `${seat.seat_id} — נמכר ${Math.round(seat.confidence * 100)}%`
                          : `${seat.seat_id} — פנוי`,
                      })
                    }
                  />
                );
              })}
            </g>
          );
        })}
      </svg>
      {tooltip && (
        <div style={{
          position: 'fixed',
          left: tooltip.x + 10,
          top: tooltip.y - 28,
          background: '#1e293b',
          color: '#f8fafc',
          padding: '3px 8px',
          borderRadius: 6,
          fontSize: 11,
          pointerEvents: 'none',
          zIndex: 9999,
          whiteSpace: 'nowrap',
        }}>
          {tooltip.text}
        </div>
      )}
      <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 10, color: TEXT_TERTIARY }}>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#ef4444', verticalAlign: 'middle', marginLeft: 3 }} />מוצע לחסימה</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#6b7280', verticalAlign: 'middle', marginLeft: 3 }} />חסום מאושר</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: '#e2e8f0', verticalAlign: 'middle', marginLeft: 3 }} />פנוי</span>
        <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: 'rgba(251,191,36,0.8)', verticalAlign: 'middle', marginLeft: 3 }} />נמכר לפעמים</span>
      </div>
    </div>
  );
}
